import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ArrowLeft, Camera, Check, Eye, EyeOff, IdCard, KeyRound, LoaderCircle, LockKeyhole, Mail, Phone, RefreshCw, Settings2, ShieldCheck, Upload, UserRound } from 'lucide-react'
import WorkspaceLayout from 'components/Erp/WorkspaceLayout'
import UserAvatar from 'components/Erp/UserAvatar'
import { PageHeader, SectionHeading } from 'components/Erp/Presentation'
import { getCurrentUser } from 'service/AuthService'
import { updateProfilePhoto, updateProfilePin, updateProfilePassword } from 'service/AccountSettingsService'
import { getErpErrorMessage } from 'service/ErpService'
import { updateSessionTokens } from 'http/client'
import { setUser } from 'store/slices/UserSlice'
import { getRoleLabel } from 'utils/access'
import styles from './AccountSettingsPage.module.scss'

const readProfile = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}') || {} }
  catch { return {} }
}

function SecretField({ label, value, onChange, disabled, pin = false, maxLength, autoComplete = 'off' }) {
  useInterfaceLanguage();
  const [visible, setVisible] = useState(false)
  return <label className={styles.field}>
    <span>{tr(label)}</span>
    <span className={styles.secretInput}>
      <input required type={visible ? 'text' : 'password'} value={value} onChange={event => onChange(event.target.value)}
        disabled={disabled} inputMode={pin ? 'numeric' : undefined} maxLength={maxLength} autoComplete={autoComplete} />
      <button type="button" disabled={disabled} aria-label={`${visible ? tr("Скрыть") : tr("Показать")}: ${label}`} aria-pressed={visible} onClick={() => setVisible(previous => !previous)}>
        {visible ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}
      </button>
    </span>
  </label>
}

function FormMessage({ message }) {
  useInterfaceLanguage();
  return message?.text ? <p className={message.error ? styles.error : styles.success} role={message.error ? 'alert' : 'status'}>{tr(!message.error && <Check size={15} aria-hidden="true" />)}{tr(message.text)}</p> : null
}

export default function AccountSettingsPage() {
  useInterfaceLanguage();
  const user = useSelector(state => state.user)
  const dispatch = useDispatch()
  const [profile, setProfile] = useState(readProfile)
  const [loading, setLoading] = useState(true)
  const [profileError, setProfileError] = useState('')
  const [busy, setBusy] = useState('')
  const [messages, setMessages] = useState({})
  const [photo, setPhoto] = useState(null)
  const [preview, setPreview] = useState('')
  const [oldPin, setOldPin] = useState('')
  const [newPin, setNewPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [passwordPin, setPasswordPin] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const mounted = useRef(false)
  const operation = useRef(0)
  const photoInput = useRef(null)
  const userId = user.userId
  const message = (key, text, error = false) => setMessages(previous => ({ ...previous, [key]: { text, error } }))
  const syncProfile = useCallback(data => {
    const next = Object.fromEntries(Object.entries(data).filter(([key]) => !['pin', 'old_pin', 'new_pin', 'new_pin_confirm', 'password', 'password_confirm', 'new_password'].includes(key)))
    next.token = localStorage.getItem('token')
    setProfile(next)
    dispatch(setUser(next))
    localStorage.setItem('user', JSON.stringify(next))
  }, [dispatch])
  const refreshProfile = useCallback(async () => {
    const { data } = await getCurrentUser()
    if (!mounted.current || (data.user ?? data.user_id) !== userId) return
    syncProfile(data)
    setProfileError('')
  }, [syncProfile, userId])
  const loadProfile = useCallback(async () => {
    setLoading(true)
    setProfileError('')
    try { await refreshProfile() }
    catch (error) { if (mounted.current) setProfileError(getErpErrorMessage(error, tr("Не удалось обновить данные профиля."))) }
    finally { if (mounted.current) setLoading(false) }
  }, [refreshProfile])
  useEffect(() => {
    mounted.current = true
    loadProfile()
    return () => { mounted.current = false; operation.current += 1 }
  }, [loadProfile])
  useEffect(() => {
    if (!photo) { setPreview(''); return }
    const source = URL.createObjectURL(photo)
    setPreview(source)
    return () => URL.revokeObjectURL(source)
  }, [photo])
  const disabled = loading || Boolean(busy)
  const hasPin = Boolean(user.has_pin)
  const name = [user.surName, user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || tr("Пользователь")
  const perform = async (key, action, optimistic, success, reset) => {
    if (disabled) return
    const sequence = ++operation.current
    setBusy(key)
    message(key, '')
    try {
      const { data } = await action()
      if (!mounted.current || sequence !== operation.current) return
      updateSessionTokens(data)
      syncProfile({ ...readProfile(), ...optimistic(data), token: localStorage.getItem('token') })
      reset()
      message(key, success)
      try { await refreshProfile() }
      catch { if (mounted.current) setProfileError(tr("Изменения сохранены. Не удалось обновить данные профиля; повторите загрузку.")) }
    } catch (error) {
      if (mounted.current && sequence === operation.current)
        message(key, getErpErrorMessage(error, tr("Не удалось сохранить изменения. Проверьте соединение и повторите.")), true)
    } finally { if (mounted.current && sequence === operation.current) setBusy('') }
  }
  const selectPhoto = event => {
    const selected = event.target.files?.[0]
    event.target.value = ''
    if (!selected) return
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(selected.type)) {
      message('photo', tr("Выберите фотографию в формате JPEG, PNG или WebP."), true)
      return
    }
    if (selected.size > 5 * 1024 * 1024) {
      message('photo', tr("Размер фотографии должен быть не больше 5 МБ."), true)
      return
    }
    setPhoto(selected)
    message('photo', '')
  }
  const savePin = event => {
    event.preventDefault()
    if (hasPin && !/^\d{4,8}$/.test(oldPin)) { message('pin', tr("Введите текущий PIN-код из 4–8 цифр."), true); return }
    if (!/^\d{4}$/.test(newPin)) { message('pin', tr("Новый PIN-код должен содержать четыре цифры."), true); return }
    if (newPin !== confirmPin) { message('pin', tr("Новые PIN-коды не совпадают."), true); return }
    perform('pin', () => updateProfilePin({ old_pin: hasPin ? oldPin : '', new_pin: newPin, new_pin_confirm: confirmPin }),
      () => ({ has_pin: true }), hasPin ? tr("PIN-код изменён.") : tr("PIN-код создан."), () => { setOldPin(''); setNewPin(''); setConfirmPin('') })
  }
  const savePassword = event => {
    event.preventDefault()
    if (!/^\d{4,8}$/.test(passwordPin)) { message('password', tr("Введите текущий PIN-код из 4–8 цифр."), true); return }
    if (password.length < 8) { message('password', tr("Новый пароль должен содержать не меньше восьми символов."), true); return }
    if (password !== confirmPassword) { message('password', tr("Новые пароли не совпадают."), true); return }
    perform('password', () => updateProfilePassword({ pin: passwordPin, password, password_confirm: confirmPassword }),
      () => ({ has_password: true }), tr("Пароль сохранён."), () => { setPasswordPin(''); setPassword(''); setConfirmPassword('') })
  }
  return <WorkspaceLayout><div className={styles.page}>
    <PageHeader icon={Settings2} title={tr("Параметры и личные данные")} description={tr("Фотография профиля и данные для входа в вашу учётную запись.")}
      actions={<Link to="/profile/" className={styles.back}><ArrowLeft size={15} aria-hidden="true" />{tr("Мой профиль")}</Link>} />
    {tr(profileError && <div className={styles.profileError} role="alert">{tr(profileError)}<button type="button" className={styles.secondary} disabled={disabled} onClick={loadProfile}><RefreshCw size={14} aria-hidden="true" />{tr("Повторить загрузку")}</button></div>)}
    <div className={styles.grid}>
      <section className={styles.card}>
        <SectionHeading icon={UserRound} title={tr("Личные данные")} />
        <div className={styles.identity}><UserAvatar user={user} size={76} /><div><strong>{tr(name)}</strong><span>{getRoleLabel(user)}</span></div></div>
        <dl className={styles.details}>
          <div><dt><IdCard size={15} aria-hidden="true" />{tr("ИНН / логин")}</dt><dd>{tr(profile.inn || user.username || '—')}</dd></div>
          <div><dt><Phone size={15} aria-hidden="true" />{tr("Телефон")}</dt><dd>{tr(user.numberPhone || tr("Не указан"))}</dd></div>
          <div><dt><Mail size={15} aria-hidden="true" />{tr("Электронная почта")}</dt><dd>{tr(user.email || tr("Не указана"))}</dd></div>
        </dl>
        <div className={styles.securityStatus}><ShieldCheck size={17} aria-hidden="true" /><span>{hasPin ? tr("PIN-код настроен") : tr("PIN-код ещё не настроен")}<small>{user.has_password ? tr("Пароль для входа установлен") : tr("Пароль для входа можно создать")}</small></span></div>
      </section>
      <section className={styles.card}>
        <SectionHeading icon={Camera} title={tr("Фото профиля")} description={tr("Фотография отображается в верхней панели и вашем профиле.")} />
        <div className={styles.photoEditor}>{preview ? <img src={preview} className={styles.photoPreview} alt={tr("Предпросмотр нового фото профиля")} /> : <UserAvatar user={user} size={120} />}<div><strong>{photo ? photo.name : tr("Ваше фото")}</strong><p>{tr("JPEG, PNG или WebP · до 5 МБ")}</p><input ref={photoInput} className={styles.fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={selectPhoto} disabled={disabled} aria-label={tr("Новое фото профиля")} /><button type="button" className={styles.secondary} disabled={disabled} onClick={() => photoInput.current?.click()}><Upload size={15} aria-hidden="true" />{tr("Выбрать фото")}</button></div></div>
        <FormMessage message={messages.photo} />
        <div className={styles.formFooter}>{tr(photo && <button type="button" className={styles.secondary} disabled={disabled} onClick={() => { setPhoto(null); message('photo', '') }}>{tr("Отмена")}</button>)}<button type="button" className={styles.primary} disabled={disabled || !photo} onClick={() => perform('photo', () => updateProfilePhoto(photo), data => ({ avatar_url: data.avatar_url || data.photo_url || data.imeag, photo_url: data.photo_url || data.avatar_url || data.imeag, imeag: data.imeag || data.avatar_url || data.photo_url }), tr("Фото профиля обновлено."), () => setPhoto(null))}>{busy === 'photo' ? <LoaderCircle size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}{busy === 'photo' ? tr("Сохранение…") : tr("Сохранить фото")}</button></div>
      </section>
      <section className={styles.card}>
        <SectionHeading icon={KeyRound} title={hasPin ? tr("Смена PIN-кода") : tr("Создание PIN-кода")} description={hasPin ? tr("Подтвердите текущий PIN-код и задайте новый из четырёх цифр.") : tr("Создайте PIN-код из четырёх цифр для входа в систему.")} />
        <form onSubmit={savePin} aria-label={tr("Настройка PIN-кода")}>
          {tr(hasPin && <SecretField label={tr("Текущий PIN-код")} pin maxLength={8} value={oldPin} onChange={setOldPin} disabled={disabled || profile.can_change_pin === false} />)}
          <SecretField label={tr("Новый PIN-код")} pin maxLength={4} value={newPin} onChange={setNewPin} disabled={disabled || profile.can_change_pin === false} />
          <SecretField label={tr("Повторите новый PIN-код")} pin maxLength={4} value={confirmPin} onChange={setConfirmPin} disabled={disabled || profile.can_change_pin === false} />
          <FormMessage message={messages.pin} />
          <div className={styles.formFooter}><button className={styles.primary} disabled={disabled || profile.can_change_pin === false}>{busy === 'pin' ? <LoaderCircle size={16} aria-hidden="true" /> : <KeyRound size={16} aria-hidden="true" />}{busy === 'pin' ? tr("Сохранение…") : hasPin ? tr("Изменить PIN-код") : tr("Создать PIN-код")}</button></div>
        </form>
      </section>
      <section className={styles.card}>
        <SectionHeading icon={LockKeyhole} title={user.has_password ? tr("Смена пароля") : tr("Создание пароля")} description={tr("Введите текущий PIN-код и задайте новый пароль для входа.")} tone="teal" />
        {tr(!hasPin && <p className={styles.hint}>{tr("Сначала создайте PIN-код в соседнем разделе.")}</p>)}
        <form onSubmit={savePassword} aria-label={tr("Настройка пароля")}>
          <SecretField label={tr("PIN-код для смены пароля")} pin maxLength={8} value={passwordPin} onChange={setPasswordPin} disabled={disabled || !hasPin} />
          <SecretField label={tr("Новый пароль")} value={password} onChange={setPassword} disabled={disabled || !hasPin} autoComplete="new-password" />
          <SecretField label={tr("Повторите новый пароль")} value={confirmPassword} onChange={setConfirmPassword} disabled={disabled || !hasPin} autoComplete="new-password" />
          <p className={styles.hint}>{tr("Не меньше восьми символов.")}</p>
          <FormMessage message={messages.password} />
          <div className={styles.formFooter}><button className={styles.primary} disabled={disabled || !hasPin}>{busy === 'password' ? <LoaderCircle size={16} aria-hidden="true" /> : <LockKeyhole size={16} aria-hidden="true" />}{busy === 'password' ? tr("Сохранение…") : tr("Сохранить пароль")}</button></div>
        </form>
      </section>
    </div>
  </div></WorkspaceLayout>
}
